import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, JSX } from 'react';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { CreateAutoBalanceRequest } from './types';
import { usePendingData } from '../../../../providers/PendingData.provider';
import CreateTransferDialouge from '../Dialogue/CreateTransfer/CreateTransferDialouge';
import CreateAutoBalanceDialogueContext, {
  CreateAutoBalanceCtx,
} from '../Dialogue/CreateTransfer/CreateTransferContext';
import { CreateAutoBalanceDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import usePostCreateAutoBalanceTransfer from '../../../../hooks/useApi/usePostCreateAutoBalanceTransfer';
import { useTranslation } from 'react-i18next';

const { Box, SubTitle, Table, TextField, formattedCurrency } = vp;

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
};

type CreateAutoBalanceProps = {
  defaultValues: CreateAutoBalanceRequest;
  submitTransaction: boolean;
};

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
};

type FC = FunctionComponent<CreateAutoBalanceProps>;

const CreateAutoBalanceTransferReview: FC = (props: CreateAutoBalanceProps) => {
  const { defaultValues, submitTransaction } = props;
  const history = useHistory();
  const [autoBalanceResponse, postAutoBalance] = usePostCreateAutoBalanceTransfer({});
  const { reset, hasPendingData } = usePendingData();
  const [shouldCreatedAutoBalance, setShouldCreatedAutoBalance] = useState(false);
  const [showCreateAutoBalanceErrorAlert, setShowCreateAutoBalanceErrorAlert] = useState(false);
  const [createAutoBalanceError, setCreateAutoBalanceError] = useState('');
  const [autobalanceCreated, setAutobalanceCreated] = useState(false);
  const CreateAutoBalance: CreateAutoBalanceCtx = {
    showCreateAutoBalanceErrorAlert,
    createAutoBalanceError,
  };

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.Create.Form' });

  const handlers = {
    handleCreateAutoBalanceDialogue: (handlerProps: DialogueHandlerProps<CreateAutoBalanceDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;
      const dialogueHandlers: CreateAutoBalanceDialogueHandler = {
        confirm: {
          confirm: {
            default: () => {
              setShowCreateAutoBalanceErrorAlert(false);
              history.push('/auto-balance-transfer');
            },
          },
          fail: {
            default: () => {
              setShowCreateAutoBalanceErrorAlert(false);
              history.push('/auto-balance-transfer');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    onCreateAutoBalance: (_values: CreateAutoBalanceRequest) => {
      setShouldCreatedAutoBalance(true);

      postAutoBalance({
        ScheduleStartDate: moment(Number(defaultValues.StartDate)).format('YYYY-MM-DD') || '',
        AutoBalanceTransferAmount: defaultValues.Balance || 0.0,
        TypeOfFrequency: defaultValues.Frequency || '',
      });
    },
  };

  useEffect(() => {
    if (shouldCreatedAutoBalance) {
      const { Success, ErrorMessage } = autoBalanceResponse;
      if (Success === true) {
        setAutobalanceCreated(true);
        reset();
      } else if (Success === false) {
        setShowCreateAutoBalanceErrorAlert(true);
        setCreateAutoBalanceError(ErrorMessage);
      }
      setShouldCreatedAutoBalance(false);
    }
  }, [autoBalanceResponse]);

  useEffect(() => {
    if (!hasPendingData && defaultValues && autobalanceCreated) {
      history.push('/auto-balance-transfer/overview');
    }
  }, [hasPendingData, autobalanceCreated]);
  useEffect(() => {
    if (submitTransaction && defaultValues) {
      handlers.onCreateAutoBalance(defaultValues);
    }
  }, [submitTransaction]);
  return (
    <Formik initialValues={defaultValues} onSubmit={handlers.onCreateAutoBalance} validateOnChange validateOnBlur>
      {({ handleSubmit }) => (
        <CreateAutoBalanceDialogueContext.Provider value={CreateAutoBalance}>
          <form onSubmit={handleSubmit}>
            <Box flx="col">
              <Box flx="col" width="50%" mt="md">
                <SubTitle subTitle={t('subTitle')} variant="small" />
                <Box mb="md">
                  <Table {...getTableProps('transactionDetails')} />
                </Box>
              </Box>
            </Box>
          </form>
          <CreateTransferDialouge handleDialogue={handlers.handleCreateAutoBalanceDialogue} />
        </CreateAutoBalanceDialogueContext.Provider>
      )}
    </Formik>
  );
  function getParseLabel(frequency: string): string {
    return frequency === 'biweekly' ? 'Bi-Weekly' : frequency;
  }
  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers2: TablePropsHandlers = {
      transactionDetails: () => {
        const TransactionDetailsPair: KeyValuePair[] = [
          {
            display: t('currency'),
            value: (
              <TextField
                name="currency-confirmation"
                variant="background"
                placeholder={defaultValues.Currency || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: `${t('Balance.label')} ${t('Balance.instructions')}`,
            value: (
              <TextField
                name="amount-confirmation"
                variant="background"
                placeholder={`$${formattedCurrency(String(defaultValues.Balance))}` || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('frequency'),
            value: (
              <TextField
                name="frequency-confirmation"
                variant="background"
                placeholder={getParseLabel(defaultValues.Frequency || '')}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: t('StartDate.label'),
            value: (
              <TextField
                name="start-date-confirmation"
                variant="background"
                placeholder={moment(Number(defaultValues.StartDate)).format('YYYY-MM-DD') || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
        ];

        const transactionDetails: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Transaction Details' },
            detailValue: { header: '' },
          },
          rows:
            TransactionDetailsPair.map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })) || [],
        };

        return transactionDetails;
      },
    };

    return handlers2[handler]();
  }
};

export default CreateAutoBalanceTransferReview;
